/**
 * 🎛️ PERSONALITY SLIDER HOOK
 * Unified state management for personality sliders
 * Replaces 4 separate state variables with clean, unified API
 */

import { useState, useCallback } from 'react';
import {
  PersonalitySliderState,
  PersonalitySliderActions,
  PersonalitySliderHookReturn,
  SliderConfig,
  defaultPersonalitySliderState,
  defaultSliderConfiguration
} from '../types/PersonalitySliderTypes';
import { storage } from '../services/storage';

export const usePersonalitySliders = (): PersonalitySliderHookReturn => {
  const [state, setState] = useState<PersonalitySliderState>(defaultPersonalitySliderState);

  // Show modal with configuration
  const showModal = useCallback((config: SliderConfig[] = defaultSliderConfiguration) => {
    setState(prev => ({
      ...prev,
      isVisible: true,
      mode: 'modal',
      configuration: config,
      values: config.reduce((acc, slider) => ({
        ...acc,
        [slider.id]: slider.value
      }), {}),
      error: null
    }));
  }, []);

  // Show inline with configuration
  const showInline = useCallback((config: SliderConfig[] = defaultSliderConfiguration) => {
    setState(prev => ({
      ...prev,
      isVisible: true,
      mode: 'inline',
      configuration: config,
      values: config.reduce((acc, slider) => ({
        ...acc,
        [slider.id]: slider.value
      }), {}),
      error: null
    }));
  }, []);

  // Hide sliders
  const hide = useCallback(() => {
    setState(prev => ({
      ...prev,
      isVisible: false,
      mode: 'hidden',
      isSubmitting: false,
      error: null
    }));
  }, []);

  // Update single slider value
  const updateValue = useCallback((sliderId: string, value: number) => {
    setState(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [sliderId]: value
      }
    }));
  }, []);

  // Update multiple slider values
  const updateValues = useCallback((values: Record<string, number>) => {
    setState(prev => ({
      ...prev,
      values: {
        ...prev.values,
        ...values
      }
    }));
  }, []);

  // Submit sliders - accept values as parameter to avoid race condition
  const submit = useCallback(async (mode: 'modal' | 'inline', explicitValues?: Record<string, number>) => {
    setState(prev => ({ ...prev, isSubmitting: true, error: null }));
    
    try {
      // Get JWT token
      const jwtToken = await storage.getItem('jwt_token');
      if (!jwtToken) {
        throw new Error('No authentication token found');
      }

      // Use explicitly passed values or fall back to state values
      const valuesToSubmit = explicitValues || state.values;

      // Prepare submission data
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({
          message: "I've completed setting my coaching style preferences. Let's move forward with my fitness journey.",
          file_insights: [],
          frontend_state: {
            personality_sliders_submitted: true,
            personality_preferences: valuesToSubmit,
            next_expected: "discovery_phase",
            slider_completion_context: "User just finished adjusting personality sliders and expects to move to personal discovery questions about age, injuries, etc."
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🎛️ PERSONALITY SLIDER SUBMISSION: Success', data);

      // Update state to reflect successful submission
      setState(prev => ({
        ...prev,
        isSubmitting: false,
        hasSubmitted: true,
        isVisible: false,
        mode: 'hidden'
      }));

      // Return response data for further processing
      return data;

    } catch (error) {
      console.error('🎛️ PERSONALITY SLIDER SUBMISSION: Error', error);
      setState(prev => ({
        ...prev,
        isSubmitting: false,
        error: error instanceof Error ? error.message : 'Submission failed'
      }));
      throw error;
    }
  }, [state.values]);

  // Reset state
  const reset = useCallback(() => {
    setState(defaultPersonalitySliderState);
  }, []);

  // Set error
  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  // Actions object
  const actions: PersonalitySliderActions = {
    showModal,
    showInline,
    hide,
    updateValue,
    updateValues,
    submit,
    reset,
    setError
  };

  return { state, actions };
};

// Utility functions for backward compatibility
export const parseSliderAction = (action: any): SliderConfig[] => {
  if (!action || !action.sliders) {
    return defaultSliderConfiguration;
  }

  return action.sliders.map((slider: any) => ({
    id: slider.id || 'unknown',
    label: slider.label || 'Unknown',
    min: slider.min || 1,
    max: slider.max || 10,
    value: slider.value || 5,
    description: slider.description || ''
  }));
};

export const createSliderConfiguration = (
  currentValues: Record<string, number> = {}
): SliderConfig[] => {
  return defaultSliderConfiguration.map(slider => ({
    ...slider,
    value: currentValues[slider.id] || slider.value
  }));
};
